// Staff area: sign-in gate, navy left nav, "Outlook disconnected" banner.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Activity, AtSign, ChartColumn, House, ListChecks, LogOut, Megaphone, MessageSquare, Rocket, Settings, Users, X, type LucideIcon } from 'lucide-react';
import type { HomeResponse, MeResponse, OnboardingStatus, PipelineRow } from '@upm/domain';
import { api } from '../lib/api';
import { APP_NAME, authMode, currentSession, isPlatformAdmin, setSignInRedirect, signOut } from '../lib/auth';
import { loadConfig } from '../lib/config';
import { Avatar, Banner, Spinner, Wordmark, cx, initialsOf } from '../ui';
import { NoAccess, signInPath } from './SignIn';

type MeCtx = {
  me: MeResponse | null; reloadMe: () => Promise<void>; reportPipeline: (rows: PipelineRow[]) => void;
  /** Home's "Needs you" count for the nav badge: pass a fresh count, or nothing to refetch it. */
  reportNeedsYou: (n?: number) => void;
  /** Requests with firm activity you haven't opened (the Feedback badge), and a refetch of it. */
  feedbackNew: number | null;
  reportFeedbackSeen: () => void;
  /** Refetch the My work badge: unread tags plus followed items with new comments. */
  reportWorkSeen: () => void;
};
const Me = createContext<MeCtx>({
  me: null, reloadMe: async () => {}, reportPipeline: () => {}, reportNeedsYou: () => {}, feedbackNew: null, reportFeedbackSeen: () => {}, reportWorkSeen: () => {},
});
export const useMe = () => useContext(Me);

/** Handed off and withdrawn onboardings are archived; everything else counts as active. */
export const ARCHIVED: ReadonlySet<OnboardingStatus> = new Set(['handed_off', 'withdrawn']);
const activeCount = (rows: PipelineRow[]) => (Array.isArray(rows) ? rows : []).filter((r) => !ARCHIVED.has(r.status)).length;

const navItem = (active: boolean) => cx(
  'flex min-h-11 items-center gap-2.5 whitespace-nowrap rounded-[10px] px-3 text-sm no-underline',
  active ? 'bg-nav-active font-semibold text-white shadow-[inset_0_0_0_1px_rgb(var(--nav-ring))] hover:text-white' : 'font-medium text-header-nav hover:bg-nav-active/60 hover:text-white',
);

// "Outlook disconnected" can be dismissed. It stays hidden in this browser until the reason changes (a new
// error, or connected and lost again), so a fresh problem still shows.
const outlookKey = (email: string) => `upm:outlook-banner-dismissed:${email.toLowerCase()}`;
const outlookReason = (me: MeResponse) => me.graph.error ?? 'not-connected';

function NavIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon aria-hidden size={18} strokeWidth={1.8} />;
}

export function AdminShell() {
  const [gate, setGate] = useState<'checking' | 'ok' | { noAccess: string }>('checking');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<number | null>(null);
  const [needsYou, setNeedsYou] = useState<number | null>(null);
  const [feedbackNew, setFeedbackNew] = useState<number | null>(null);
  const [workNew, setWorkNew] = useState<number | null>(null);
  /** The Outlook problem this person dismissed (stored per browser), or null. */
  const [outlookDismissed, setOutlookDismissed] = useState<string | null>(null);
  const feedbackAsk = useRef(0);
  const workAsk = useRef(0);
  const loc = useLocation();
  const navigate = useNavigate();
  const here = useRef(loc.pathname + loc.search);
  here.current = loc.pathname + loc.search;

  const reloadMe = useCallback(async () => {
    try { setMe(await api.get<MeResponse>('/api/admin/me')); setError(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load your account.'); }
  }, []);

  useEffect(() => { document.title = APP_NAME; }, []);

  // Gate: dev mode passes; otherwise a live Cognito session in the platform-admin group.
  useEffect(() => {
    let cancelled = false;
    const toSignIn = () => navigate(signInPath(here.current), { replace: true });
    setSignInRedirect(toSignIn);
    (async () => {
      const c = await loadConfig();
      if (c.authMode === 'cognito') {
        const s = await currentSession();
        if (cancelled) return;
        if (!s) { toSignIn(); return; }
        if (!isPlatformAdmin(s)) { setGate({ noAccess: s.email }); return; }
      }
      if (cancelled) return;
      setGate('ok');
      await reloadMe();
      api.get<PipelineRow[]>('/api/admin/onboardings').then((rows) => { if (!cancelled) setActive(activeCount(rows)); }, () => {});
    })();
    return () => { cancelled = true; };
    // Runs once per mount; later 401s go through setSignInRedirect.
  }, []);

  const reportPipeline = useCallback((rows: PipelineRow[]) => setActive(activeCount(rows)), []);
  const reportNeedsYou = useCallback((n?: number) => {
    if (typeof n === 'number') { setNeedsYou(n); return; }
    api.get<HomeResponse>('/api/admin/home').then((h) => { if (typeof h?.counts?.needsYou === 'number') setNeedsYou(h.counts.needsYou); }, () => {});
  }, []);
  useEffect(() => { if (gate === 'ok') reportNeedsYou(); }, [gate, reportNeedsYou]);
  // Only the latest ask may set the badge: an older answer arriving late would show a stale count.
  const reportFeedbackSeen = useCallback(() => {
    const ask = ++feedbackAsk.current;
    api.get<{ requests: number }>('/api/admin/feedback/new')
      .then((r) => { if (ask === feedbackAsk.current && typeof r?.requests === 'number') setFeedbackNew(r.requests); }, () => {});
  }, []);
  const reportWorkSeen = useCallback(() => {
    const ask = ++workAsk.current;
    api.get<{ total: number }>('/api/admin/work/count')
      .then((r) => { if (ask === workAsk.current && typeof r?.total === 'number') setWorkNew(r.total); }, () => {});
  }, []);
  // On each page change, every minute while the tab is visible, and when it's shown again.
  useEffect(() => {
    if (gate !== 'ok') return;
    reportFeedbackSeen();
    reportWorkSeen();
    const tick = () => { if (!document.hidden) { reportFeedbackSeen(); reportWorkSeen(); } };
    const t = window.setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [gate, loc.pathname, reportFeedbackSeen, reportWorkSeen]);

  // The stored dismissal; once Outlook connects it's forgotten, so losing it later shows the banner again.
  useEffect(() => {
    if (!me) return;
    try {
      if (me.graph.connected) localStorage.removeItem(outlookKey(me.email));
      setOutlookDismissed(me.graph.connected ? null : localStorage.getItem(outlookKey(me.email)));
    } catch { /* storage unavailable: the banner shows */ }
  }, [me]);
  const dismissOutlook = () => {
    if (!me) return;
    try { localStorage.setItem(outlookKey(me.email), outlookReason(me)); } catch { /* hidden for this visit only */ }
    setOutlookDismissed(outlookReason(me));
  };

  const onSignOut = async () => {
    await signOut();
    navigate('/admin/sign-in', { replace: true });
  };

  if (gate === 'checking') return <div className="min-h-screen bg-page"><Spinner label="Loading" /></div>;
  if (typeof gate === 'object') return <NoAccess email={gate.noAccess} />;

  const homeActive = loc.pathname === '/admin' || loc.pathname === '/admin/' || loc.pathname.startsWith('/admin/home');
  const workActive = loc.pathname.startsWith('/admin/work');
  const onboardingActive = loc.pathname.startsWith('/admin/onboarding');
  const feedbackActive = loc.pathname.startsWith('/admin/feedback');
  const backlogActive = loc.pathname.startsWith('/admin/backlog');
  const releasesActive = loc.pathname.startsWith('/admin/releases');
  const announcementsActive = loc.pathname.startsWith('/admin/announcements');
  const kpisActive = loc.pathname.startsWith('/admin/kpis');
  const operationsActive = loc.pathname.startsWith('/admin/operations');
  const settingsActive = loc.pathname.startsWith('/admin/settings');

  return (
    <Me.Provider value={{ me, reloadMe, reportPipeline, reportNeedsYou, feedbackNew, reportFeedbackSeen, reportWorkSeen }}>
      <div className="flex min-h-screen flex-col bg-page text-sm md:flex-row md:items-stretch">
        <nav aria-label="Admin" className="bg-brand text-header-fg md:w-[248px] md:flex-none">
          <div className="flex flex-col gap-1.5 px-4 pb-3 pt-4 md:sticky md:top-0 md:h-screen md:overflow-y-auto md:py-[22px]">
          <div className="flex items-baseline gap-2.5 px-2.5 pb-3 pt-1 md:pb-[22px]">
            <Wordmark staff />
            <span className="border-l border-header-rule pl-2.5 text-[11px] font-semibold leading-none tracking-[0.08em] text-nav-muted">PLATFORM</span>
          </div>
          <div className="hidden px-3 py-1.5 text-[11px] font-bold tracking-[0.08em] text-nav-label md:block">WORKSPACE</div>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0 md:flex-col md:flex-nowrap">
            <li>
              <NavLink to="/admin" end aria-current={homeActive ? 'page' : undefined} className={navItem(homeActive)}>
                <NavIcon icon={House} />Home
                {needsYou !== null && needsYou > 0 && (
                  <span className="ml-auto rounded-pill bg-warn-line px-2 py-px text-xs font-bold tabular-nums text-ink">
                    {needsYou}<span className="sr-only"> need you</span>
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/work" aria-current={workActive ? 'page' : undefined} className={navItem(workActive)}>
                <NavIcon icon={AtSign} />My work
                {workNew !== null && workNew > 0 && (
                  <span className="ml-auto rounded-pill bg-warn-line px-2 py-px text-xs font-bold tabular-nums text-ink">
                    {workNew}<span className="sr-only"> new</span>
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/onboarding" aria-current={onboardingActive ? 'page' : undefined} className={navItem(onboardingActive)}>
                <NavIcon icon={Users} />Firm onboarding
                {active !== null && active > 0 && (
                  <span className="ml-auto rounded-pill bg-header-rule px-2 py-px text-xs font-semibold text-white">
                    {active}<span className="sr-only"> active</span>
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/feedback" aria-current={feedbackActive ? 'page' : undefined} className={navItem(feedbackActive)}>
                <NavIcon icon={MessageSquare} />Feedback
                {feedbackNew !== null && feedbackNew > 0 && (
                  <span className="ml-auto rounded-pill bg-warn-line px-2 py-px text-xs font-bold tabular-nums text-ink">
                    {feedbackNew}<span className="sr-only"> new</span>
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/backlog" aria-current={backlogActive ? 'page' : undefined} className={navItem(backlogActive)}>
                <NavIcon icon={ListChecks} />Backlog
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/releases" aria-current={releasesActive ? 'page' : undefined} className={navItem(releasesActive)}>
                <NavIcon icon={Rocket} />Releases
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/announcements" aria-current={announcementsActive ? 'page' : undefined} className={navItem(announcementsActive)}>
                <NavIcon icon={Megaphone} />Announcements
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/kpis" aria-current={kpisActive ? 'page' : undefined} className={navItem(kpisActive)}>
                <NavIcon icon={ChartColumn} />KPIs
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/operations" aria-current={operationsActive ? 'page' : undefined} className={navItem(operationsActive)}>
                <NavIcon icon={Activity} />Operations
              </NavLink>
            </li>
            <li>
              <NavLink to="/admin/settings" aria-current={settingsActive ? 'page' : undefined} className={navItem(settingsActive)}>
                <NavIcon icon={Settings} />Settings
              </NavLink>
            </li>
          </ul>
          <div className="mt-2 flex items-center gap-2.5 border-t border-nav-rule px-1 pb-0 pt-3 md:mt-auto md:pb-1">
            <Avatar initials={initialsOf(me?.name || me?.email)} size={34} tone="soft" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-[13px] font-semibold leading-tight text-white" title={me ? me.name || me.email : undefined}>{me ? me.name || me.email : 'Loading…'}</span>
              {me && (
                <span className="flex items-center gap-1.5 text-xs leading-tight text-nav-muted">
                  <span aria-hidden className={cx('h-1.5 w-1.5 flex-none rounded-pill', me.graph.connected ? 'bg-[#6FCF8E]' : 'bg-[#E3B572]')} />
                  {me.graph.connected ? 'Outlook connected' : 'Outlook off'}
                </span>
              )}
            </div>
            {authMode() === 'cognito' && (
              <button
                type="button"
                aria-label="Sign out"
                title="Sign out"
                onClick={() => void onSignOut()}
                className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border-0 bg-transparent text-header-nav hover:bg-nav-active hover:text-white"
              >
                <LogOut aria-hidden size={18} strokeWidth={1.8} />
              </button>
            )}
          </div>
          </div>
        </nav>
        <main className="flex min-w-0 flex-1 flex-col gap-4 px-4 pb-10 pt-5 sm:px-6 lg:px-8 lg:pt-6">
          {error && <Banner tone="error" role="alert">{error}</Banner>}
          {/* On Home the setup checklist covers a mailbox that was never connected. */}
          {me && !me.graph.connected && !(homeActive && !me.graph.error) && outlookDismissed !== outlookReason(me) && (
            <Banner tone="error" role="alert">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <span className="font-semibold">Outlook disconnected.</span> Invites and firm emails still go out from {me.systemMailbox} with replies to you. Discovery Session booking needs Outlook and falls back to “contact me”.{' '}
                  <NavLink to="/admin/settings" className="font-semibold text-bad underline">Connect Outlook</NavLink>
                  {me.graph.error && <span className="block text-[13px]">{me.graph.error}</span>}
                </div>
                <button type="button" onClick={dismissOutlook} title="Hide this until something changes. Settings still shows the Outlook connection."
                  className="-my-1 inline-flex min-h-9 flex-none items-center gap-1 rounded-lg border-0 bg-transparent px-2 text-[13px] font-semibold text-bad hover:bg-white/60">
                  <X aria-hidden size={14} /> Dismiss
                </button>
              </div>
            </Banner>
          )}
          <Outlet />
        </main>
      </div>
    </Me.Provider>
  );
}
